/**
 * Business profile tabs.
 *
 * Each fetches its own slice on demand, so opening a profile does not pull
 * products, services and the relationship graph for a reader who only wanted a
 * phone number.
 */

import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { PriceValue } from '@/components/data/prices'
import { NetworkIcon, PackageIcon } from '@/components/ui/icons'
import {
  Badge, EmptyState, Panel, SectionHeader, Skeleton,
} from '@/components/ui/primitives'
import { businessApi } from '@/services/endpoints'
import { formatMoney, formatRelative, humanise, UNKNOWN } from '@/utils/format'
import type { Relationship } from '@/types/api'

export function ProductsTab({ slug }: { slug: string }) {
  const query = useQuery({
    queryKey: ['business', slug, 'prices'],
    queryFn: () => businessApi.prices(slug, 1),
  })

  if (query.isLoading) return <Skeleton className="h-56" />

  const offers = query.data?.data ?? []
  if (offers.length === 0) {
    return (
      <EmptyState
        icon={<PackageIcon size={18} />}
        title="No products recorded"
        description="Nothing this business sells has been indexed yet."
      />
    )
  }

  return (
    <Panel padded={false}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[36rem] text-sm">
          <thead>
            <tr className="border-b border-[var(--border-default)] text-left">
              <th className="px-4 pb-2 pt-4 field-label font-semibold">Product</th>
              <th className="px-4 pb-2 pt-4 field-label font-semibold text-right">Price</th>
              <th className="px-4 pb-2 pt-4 field-label font-semibold">Terms</th>
              <th className="px-4 pb-2 pt-4 field-label font-semibold">Observed</th>
            </tr>
          </thead>
          <tbody className="divide-hairline">
            {offers.map((offer, index) => (
              <tr key={index} className="hover:bg-[var(--surface-hover)]">
                <td className="px-4 py-3">
                  {offer.product ? (
                    <Link
                      to={`/products/general/${offer.product.slug}`}
                      className="font-medium text-[var(--text-primary)] hover:text-[var(--accent-text)]"
                    >
                      {offer.product.name}
                    </Link>
                  ) : (
                    UNKNOWN
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <PriceValue offer={offer} size="sm" />
                  {offer.unit ? (
                    <p className="text-2xs text-[var(--text-tertiary)]">per {offer.unit}</p>
                  ) : null}
                </td>
                <td className="px-4 py-3">
                  <Badge tone="neutral" size="xs">{humanise(offer.price_type)}</Badge>
                </td>
                <td className="px-4 py-3 text-xs text-[var(--text-tertiary)]">
                  {formatRelative(offer.observed_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

export function ServicesTab({ slug }: { slug: string }) {
  const query = useQuery({
    queryKey: ['business', slug, 'services'],
    queryFn: () => businessApi.services(slug),
  })

  if (query.isLoading) return <Skeleton className="h-40" />

  const services = query.data ?? []
  if (services.length === 0) {
    return <EmptyState title="No services recorded" description="Nothing indexed yet." />
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {services.map((service) => (
        <Panel key={service.uuid}>
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">{service.name}</h3>
            <div className="shrink-0 text-right">
              {service.starting_price ? (
                <>
                  <p className="text-2xs text-[var(--text-tertiary)]">from</p>
                  <p className="text-sm font-semibold tabular">
                    {formatMoney(service.starting_price)}
                  </p>
                </>
              ) : (
                <p className="text-sm text-[var(--text-tertiary)]">On request</p>
              )}
            </div>
          </div>
          {service.short_description ? (
            <p className="mt-2 text-sm text-[var(--text-secondary)]">{service.short_description}</p>
          ) : null}
          {service.highlights.length > 0 ? (
            <ul className="mt-2.5 space-y-1">
              {service.highlights.map((highlight) => (
                <li key={highlight} className="text-xs text-[var(--text-tertiary)]">
                  · {highlight}
                </li>
              ))}
            </ul>
          ) : null}
          <div className="mt-3 flex flex-wrap items-center gap-2 text-2xs text-[var(--text-tertiary)]">
            <Badge tone="neutral" size="xs">{humanise(service.pricing_type)}</Badge>
            {service.service_area ? <span>{service.service_area}</span> : null}
          </div>
        </Panel>
      ))}
    </div>
  )
}

/**
 * The business graph — spec §47.
 *
 * Split by direction, because "A supplies B" and "B supplies A" are different
 * statements and a single flat list makes them indistinguishable.
 */
export function RelationshipsTab({ slug }: { slug: string }) {
  const query = useQuery({
    queryKey: ['business', slug, 'relationships'],
    queryFn: () => businessApi.relationships(slug),
  })

  if (query.isLoading) return <Skeleton className="h-40" />

  const graph = query.data
  const total = (graph?.outgoing.length ?? 0) + (graph?.incoming.length ?? 0)

  if (total === 0) {
    return (
      <EmptyState
        icon={<NetworkIcon size={18} />}
        title="No relationships recorded"
        description="Supplier, distributor and competitor links appear as they are found and confirmed."
      />
    )
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <RelationshipColumn
        title="This business →"
        description="Where this business sits in someone else's supply chain."
        relationships={graph?.outgoing ?? []}
      />
      <RelationshipColumn
        title="→ This business"
        description="Who names this business in theirs."
        relationships={graph?.incoming ?? []}
      />
    </div>
  )
}

function RelationshipColumn({
  title, description, relationships,
}: {
  title: string
  description: string
  relationships: Relationship[]
}) {
  return (
    <Panel padded={false}>
      <div className="p-4">
        <SectionHeader title={title} description={description} />
      </div>
      {relationships.length === 0 ? (
        <p className="px-4 pb-4 text-sm text-[var(--text-tertiary)]">None recorded.</p>
      ) : (
        <ul className="divide-hairline border-t border-[var(--border-subtle)]">
          {relationships.map((relationship, index) => (
            <li
              key={`${relationship.type}-${relationship.business?.uuid}-${index}`}
              className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--surface-hover)]"
            >
              <Badge tone="neutral" size="xs" className="shrink-0">
                {humanise(relationship.type)}
              </Badge>
              <div className="min-w-0 flex-1">
                {relationship.business ? (
                  <Link
                    to={relationship.business.canonical_path}
                    className="block truncate text-sm text-[var(--text-primary)] hover:text-[var(--accent-text)]"
                  >
                    {relationship.business.name}
                  </Link>
                ) : (
                  <span className="text-sm text-[var(--text-tertiary)]">{UNKNOWN}</span>
                )}
                {relationship.business?.location ? (
                  <p className="truncate text-2xs text-[var(--text-tertiary)]">
                    {relationship.business.location}
                  </p>
                ) : null}
              </div>
              {/* An unverified edge is shown, but never without the caveat. */}
              {relationship.verified ? (
                <Badge tone="verified" size="xs">Confirmed</Badge>
              ) : (
                <Badge tone="neutral" size="xs" title="Inferred from a source, not yet confirmed">
                  Inferred
                </Badge>
              )}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  )
}
